import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, setToken } from './client';
import type {
  AuditEntry,
  DashboardData,
  MemberDetail,
  MemberListItem,
  MeResponse,
  Section,
  StatsFull,
  UserListItem,
} from './types';

export const queryKeys = {
  me: ['me'] as const,
  sections: ['sections'] as const,
  members: (filters?: Record<string, string | undefined>) => ['members', filters ?? {}] as const,
  member: (id: string) => ['member', id] as const,
  users: ['users'] as const,
  dashboard: ['stats', 'dashboard'] as const,
  statsFull: ['stats', 'full'] as const,
  audit: (filters?: Record<string, string | undefined>) => ['audit', filters ?? {}] as const,
  config: ['config'] as const,
};

export function useAppConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: () => api.get<Record<string, string>>('/config'),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAppConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, string>) => api.patch<{ ok: true }>('/config', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.config }),
  });
}

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/auth/me');
      } catch (e) {
        // Bei 401 lokalen Token clearen, damit User zurück zum Login-Screen kommt
        if (e instanceof ApiError && e.status === 401) setToken(null);
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useSections() {
  return useQuery({
    queryKey: queryKeys.sections,
    queryFn: () => api.get<{ sections: Section[] }>('/sections/').then((r) => r.sections),
    staleTime: 5 * 60_000,
  });
}

function qs(p: Record<string, string | undefined>) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) params.set(k, v);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useMembers(filters: { q?: string; status?: string; section?: string; sort?: string }) {
  return useQuery({
    queryKey: queryKeys.members(filters),
    queryFn: () =>
      api.get<{ members: MemberListItem[] }>(`/members/${qs(filters)}`).then((r) => r.members),
  });
}

export function useMember(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: id ? queryKeys.member(id) : ['member', 'none'],
    queryFn: () => api.get<MemberDetail>(`/members/${id}`),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => api.get<{ users: UserListItem[] }>('/users/').then((r) => r.users),
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => api.get<DashboardData>('/stats/dashboard'),
    staleTime: 30_000,
  });
}

export function useStatsFull() {
  return useQuery({
    queryKey: queryKeys.statsFull,
    queryFn: () => api.get<StatsFull>('/stats/full'),
    staleTime: 60_000,
  });
}

export function useAudit(filters: { q?: string; aktion?: string; user?: string; datensatz_id?: string; tabelle?: string }) {
  return useQuery({
    queryKey: queryKeys.audit(filters),
    queryFn: () => api.get<{ entries: AuditEntry[] }>(`/audit/${qs(filters)}`).then((r) => r.entries),
  });
}

// ----- Mutations -----

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: any) => api.post<{ id: string; mitgliedsnummer: number }>('/members/', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export interface ImportRowResult {
  zeile: number;
  name: string;
  status: 'neu' | 'sektion_ergaenzt' | 'vorhanden' | 'fehler';
  hinweis?: string;
}

export interface ImportResponse {
  dryRun: boolean;
  summary: { total: number; created: number; sectionAdded: number; unchanged: number; failed: number };
  results: ImportRowResult[];
}

export function useImportMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { rows: Record<string, string>[]; dryRun?: boolean }) =>
      api.post<ImportResponse>('/members/import', input),
    onSuccess: (res) => {
      if (res.dryRun) return;
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
      qc.invalidateQueries({ queryKey: queryKeys.statsFull });
    },
  });
}

export function useUpdateMember(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: any) => api.patch<{ ok: true }>(`/members/${id}`, input),
    onMutate: async (input: any) => {
      await qc.cancelQueries({ queryKey: queryKeys.member(id) });
      const prev = qc.getQueryData<MemberDetail>(queryKeys.member(id));
      if (prev) {
        const merged: MemberDetail = { ...prev };
        // Strip IBAN from the optimistic merge — backend decides masked/encrypted state.
        const { iban, ...rest } = input;
        Object.assign(merged, rest);
        if ('eingetreten_am' in input) merged.eingetretenAm = input.eingetreten_am;
        if ('ausgetreten_am' in input) merged.ausgetretenAm = input.ausgetreten_am;
        qc.setQueryData<MemberDetail>(queryKeys.member(id), merged);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.member(id), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: queryKeys.member(id) });
    },
  });
}

export function useArchiveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`/members/${id}/archive`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

/** Macht ein Archivieren rückgängig — sonst wäre der Austritt eine Einbahnstraße. */
export function useReactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ ok: true }>(`/members/${id}`, { status: 'aktiv', ausgetreten_am: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useDeleteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/members/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: any) => api.post<{ id: string; tempPasswort: string }>('/users/', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (id: string) => api.post<{ tempPasswort: string }>(`/users/${id}/reset-password`),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) => api.patch<{ ok: true }>(`/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useAddMembership(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { section: string; status?: string; funktion?: string }) =>
      api.post<{ ok: true }>(`/members/${memberId}/memberships`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.member(memberId) });
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateMembership(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ section, patch }: { section: string; patch: { status?: string; notizen?: string; funktion?: string } }) =>
      api.patch<{ ok: true }>(`/members/${memberId}/memberships/${section}`, patch),
    onMutate: async ({ section, patch }) => {
      await qc.cancelQueries({ queryKey: queryKeys.member(memberId) });
      const prev = qc.getQueryData<MemberDetail>(queryKeys.member(memberId));
      if (prev) {
        qc.setQueryData<MemberDetail>(queryKeys.member(memberId), {
          ...prev,
          memberships: prev.memberships.map((mb) =>
            mb.section === section ? { ...mb, ...patch, status: (patch.status as any) ?? mb.status, funktion: patch.funktion ?? mb.funktion } : mb,
          ),
          sectionStatuses: prev.sectionStatuses.map((mb) =>
            mb.section === section ? { ...mb, status: (patch.status as any) ?? mb.status, funktion: patch.funktion ?? mb.funktion } : mb,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.member(memberId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.member(memberId) });
      qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

export function useRemoveMembership(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (section: string) =>
      api.del<{ ok: true }>(`/members/${memberId}/memberships/${section}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.member(memberId) });
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useSetPayment(memberId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { member: string; section: string; jahr: number; betrag: number; status: 'bezahlt' | 'offen' }) =>
      api.post<{ id: string }>('/payments/', input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.member(memberId) });
      const prev = qc.getQueryData<MemberDetail>(queryKeys.member(memberId));
      if (prev) {
        const idx = prev.payments.findIndex((p) => p.section === input.section && p.jahr === input.jahr);
        const next = [...prev.payments];
        const bezahltAm = input.status === 'bezahlt' ? new Date().toISOString().slice(0, 10) : null;
        if (idx >= 0) {
          next[idx] = { ...next[idx]!, status: input.status, betrag: input.betrag, bezahlt_am: bezahltAm };
        } else {
          next.unshift({
            id: `tmp_${Date.now()}`,
            section: input.section,
            jahr: input.jahr,
            betrag: input.betrag,
            status: input.status,
            bezahlt_am: bezahltAm,
          });
        }
        qc.setQueryData<MemberDetail>(queryKeys.member(memberId), { ...prev, payments: next });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.member(memberId), ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.member(memberId) }),
  });
}

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: any) => api.post<{ id: string }>('/sections/', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.sections }),
  });
}

export function useUpdateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) => api.patch<{ ok: true }>(`/sections/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.sections });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { vorname?: string; nachname?: string; telefon?: string }) =>
      api.patch<{ ok: true }>('/users/me', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ sessions: SessionItem[]; current: string }>('/auth/sessions'),
  });
}

export interface SessionItem {
  id: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  current: boolean;
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/auth/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}
